import HOOK from "../../components/hooks"
import { CATEGORY, COLOR, COLUMN, COUNTRY, FORMATTING, JARGON, LAYOUT } from "../../modules/constants"
import { CHECKBOX } from "../../modules/checkbox_constants"
import { addPaddingRightToLastCell, calcStats, getCellWithTooltip, headCell, setStats } from "../../modules/table.funcs"
import { formatInteger, formatFloat, getCheckboxName, nundefined } from "../../modules/utilities"

const { border1, center, country, left, right, string } = FORMATTING
const borderLeft = border1

// Creates the home page LSE Companies table (headers and rows) from lse.companiesMeta - the 'items'

//#region Header
export const createLseCompaniesHeadersData = props => {
    // console.log('createCompaniesHeadersData props:', props)
    const { portrait, showLogos, the350club } = props

    const companyHead = headCell({
        id: COLUMN.COMPANY,
        align: left,
    })

    const countryHead = headCell({
        align: center,
        id: COLUMN.COUNTRY_OF_INCORPORATION,
    })

    const employeesHead = headCell({
        borderLeft,
        id: COLUMN.EMPLOYEES,
    })

    const epicHead = headCell({
        align: left,
        id: COLUMN.EPIC,
    })

    const indexHead = headCell({
        align: left,
        id: COLUMN.LSE_INDEX,
    })

    const industryHead = headCell({
        align: left,
        id: COLUMN[HOOK.INDUSTRY],
    })

    const logoHead = headCell({
        align: left,
        id: COLUMN.LOGO,
        width: LAYOUT.LOGO_WIDTH,
    })

    const marketCapHead = headCell({
        id: COLUMN.MARKET_CAP,
    })

    const revenueHead = headCell({
        id: COLUMN.REVENUE,
    })

    const starHead = headCell({
        align: left,
        id: COLUMN.STAR,
        width: LAYOUT.STAR_WIDTH,
    })

    const headCells = portrait
        ? [starHead, companyHead, epicHead]
        : [starHead, companyHead, epicHead, indexHead, industryHead, countryHead, employeesHead, marketCapHead, revenueHead]

    if (showLogos)
        headCells.splice(1, 0, logoHead) // insert logo header

    if (the350club)
        headCells.shift() // Remove the starHead

    addPaddingRightToLastCell(headCells)
    return headCells
}
//#endregion Header

//#region Footers
export const createLseCompaniesFooterData = props => {
    // console.log('createLseCompaniesFooterwData props:', props)
    const { portrait, showLogos, stats, the350club } = props
    const label = ""

    const starFoot = headCell({ id: COLUMN.STAR, label })
    const logoFoot = headCell({ id: COLUMN.LOGO, label })
    const epicFoot = headCell({ id: COLUMN.EPIC, label })
    const nameFoot = headCell({ id: COLUMN.NAME, label })

    for (const property in stats)
        calcStats(stats[property])

    const percentGB = stats.countries && !nundefined(stats.countries.mean) ? 100 * stats.countries.mean : undefined

    const lseCompaniesNameFoot = headCell({ id: COLUMN.NAME, align: left, label: `${formatInteger(stats.numRows)} ${stats.numRows === 1 ? JARGON.COMPANY : JARGON.COMPANIES}` })
    const indexFoot = headCell({ id: COLUMN.LSE_INDEX, label })

    const industry = {
        total: headCell({
            id: COLUMN[HOOK.INDUSTRY],
            label: "Total:",
        }),
        mean: headCell({
            id: COLUMN[HOOK.INDUSTRY],
            label: "Average:",
        }),
    }

    const country = {
        total: headCell({
            id: COLUMN.COUNTRY_OF_INCORPORATION,
            align: center,
            label: `${formatInteger(stats.countries.total)} UK`,
        }),
        mean: headCell({
            id: COLUMN.COUNTRY_OF_INCORPORATION,
            align: center,
            label: `${formatFloat(percentGB, 1)}% UK`,
        }),
    }

    const employees = {
        total: headCell({
            borderLeft,
            id: COLUMN.EMPLOYEES,
            label: formatInteger(stats.employees.total),
        }),
        mean: headCell({
            borderLeft,
            id: COLUMN.EMPLOYEES,
            label: formatInteger(stats.employees.mean),
        }),
    }

    const marketCap = {
        total: headCell({
            id: COLUMN.MARKET_CAP,
            label: formatInteger(stats.marketCap.total),
        }),
        mean: headCell({
            id: COLUMN.MARKET_CAP,
            label: formatInteger(stats.marketCap.mean),
        }),
    }
    const revenue = {
        total: headCell({
            id: COLUMN.REVENUE,
            label: formatInteger(stats.revenue.total),
        }),
        mean: headCell({
            id: COLUMN.REVENUE,
            label: formatInteger(stats.revenue.mean),
        }),
    }

    const footCells = {
        totals: portrait
            ? [starFoot, lseCompaniesNameFoot, epicFoot]
            : [starFoot, lseCompaniesNameFoot, epicFoot, indexFoot, industry.total, country.total, employees.total, marketCap.total, revenue.total],
        means: portrait
            ? [starFoot, nameFoot, epicFoot]
            : [starFoot, nameFoot, epicFoot, indexFoot, industry.mean, country.mean, employees.mean, marketCap.mean, revenue.mean]
    }

    if (showLogos) {
        footCells.totals.splice(1, 0, logoFoot) // insert logo footer
        footCells.means.splice(1, 0, logoFoot) // insert logo footer
    }

    if (the350club) { // Remove the starFoot
        footCells.totals.shift()
        footCells.means.shift()
    }

    addPaddingRightToLastCell(footCells.totals)
    addPaddingRightToLastCell(footCells.means)

    return footCells
}
//#endregion Footers

//#region Rows
export const createLseCompaniesRowData = props => {
    // console.log('createLseCompaniesRowData props: ', props)
    const { items, stats } = props
    const rows = []

    items.forEach(item => {
        rows.push(createLseCompanyRow({ ...props, item }))
        ++stats.numRows
    })

    return rows
}

const createLseCompanyRow = ({ item, portrait, showLogos, stats }) => {

    const name = getCellWithTooltip({
        type: string,
        value: item.names[0],
    })

    const flag = getCellWithTooltip({
        align: center,
        sortValue: COUNTRY[item.countryCode].name,
        tooltip: `${item.names[0]} was ${JARGON.INCORPORATED.toLowerCase()} in ${COUNTRY[item.countryCode].name}`,
        type: country,
        value: item.countryCode,
    })

    const ftseIndex = getCellWithTooltip({
        sortValue: item.indexBit,
        type: string,
        value: getCheckboxName(CHECKBOX[HOOK.LSE_INDEX], item.indexBit),
    })

    const epic = getCellWithTooltip({
        tooltip: item.epics.join(', '),
        type: string,
        value: item.epics[0],
    })

    const employees = getCellWithTooltip({
        align: right,
        borderLeft,
        sortValue: item.employees ?? -1,
        type: "local",
        value: item.employees ?? JARGON.UNKNOWN,
    })

    const marketCap = getCellWithTooltip({
        align: right,
        sortValue: item.marketCap ?? Number.NEGATIVE_INFINITY,
        type: "local",
        value: item.marketCap ?? JARGON.UNKNOWN,
    })

    const revenue = getCellWithTooltip({
        align: right,
        sortValue: item.revenue ?? Number.NEGATIVE_INFINITY,
        type: "local",
        value: item.revenue ?? JARGON.UNKNOWN,
    })

    const industry = getCheckboxName(CHECKBOX[HOOK.INDUSTRY], item.industryBit)

    const industryCell = getCellWithTooltip({
        sortValue: industry === JARGON.UNKNOWN ? JARGON.LAST_STRING : industry,
        type: string,
        value: industry,
    })

    setStats(item.employees, stats.employees, true)
    setStats(item.countryCode === "GB" ? 1 : 0, stats.countries, true)
    setStats(item.marketCap, stats.marketCap, true)
    setStats(item.revenue, stats.revenue, true)

    const logo = showLogos
        ? item.logo
            ? item.logo
            : "no logo"
        : null

    const cellWithTooltips = portrait
        ? [name, epic] // portrait
        : [name, epic, ftseIndex, industryCell, flag, employees, marketCap, revenue] // landscape

    addPaddingRightToLastCell(cellWithTooltips)

    return {
        _id: item._id,
        category: CATEGORY.COMPANY,
        cellWithTooltips,
        flag,
        iconColor: COLOR.GOOD,
        ids: item.ids,
        index: item.index,
        isFavourite: item.isFavourite,
        link: `/company/${item.ids[0]}`,
        logo,
        title: item.companyName,
    }
}
//#endregion Rows

